import { config } from "@admin/config/config";

const BASE_URL = config.VITE_API_BASE_URL;

// Demo endpoints (only available in non-production environments)
// Temporary endpoints for testing purposes
export const DEMO_ENDPOINTS = {
	LOAD_DATASET: (datasetId: string) => `${BASE_URL}/demo/dataset/${datasetId}`,
} as const;

export const apiEndpoints = {
	autoAssignment: {
		regular: {
			start: `${BASE_URL}/auto-assignment/start`,
			report: `${BASE_URL}/auto-assignment/report`,
			reportSummary: `${BASE_URL}/auto-assignment/report-summary`,
			previousSummaries: `${BASE_URL}/auto-assignment/previous-summaries`,
			latestRunId: `${BASE_URL}/auto-assignment/latest-run-id`,
			verify: (departureId: number, leaderType: string, leaderId: number) =>
				`${BASE_URL}/auto-assignment/verify/${departureId}/${leaderType}/${leaderId}`,
		},
		roadScholar: {
			// Retained for the shared mutation helper and explicit fail-closed endpoint tests.
			// Product UI does not expose this disabled command.
			start: `${BASE_URL}/auto-assignment-rs/start`,
			report: `${BASE_URL}/auto-assignment-rs/report`,
			reportSummary: `${BASE_URL}/auto-assignment-rs/report-summary`,
			previousSummaries: `${BASE_URL}/auto-assignment-rs/previous-summaries`,
			insights: `${BASE_URL}/auto-assignment-rs/insights`,
			latestRunId: `${BASE_URL}/auto-assignment-rs/latest-run-id`,
			verify: (departureId: number, leaderType: string, leaderId: number) =>
				`${BASE_URL}/auto-assignment-rs/verify/${departureId}/${leaderType}/${leaderId}`,
		},
	},
	departures: {
		allCurrent: `${BASE_URL}/departures`,
		dataTable: `${BASE_URL}/departures/data-table`,
		filterOptions: `${BASE_URL}/departures/filter-options`,
		notes: (id: number) => `${BASE_URL}/departures/${id}/notes`,
		getDeparture: (id: number) => `${BASE_URL}/departures/${id}/edit`,
		updateDeparture: (id: number) => `${BASE_URL}/departures/${id}`,
		regular: {
			all: `${BASE_URL}/departures?type=regular`,
			liveViewServerTable: `${BASE_URL}/departures-live-view/data-table`,
			liveViewFilterOptions: `${BASE_URL}/departures-live-view/filter-options`,
			details: (id: number) => `${BASE_URL}/departures/${id}/details`,
			generateEligibilityReport: (id: number) =>
				`${BASE_URL}/departures/${id}/eligibility-reports/generate`,
			matchSnapshot: (id: number, roleType: "TL" | "Trainee") =>
				`${BASE_URL}/departures/${id}/matches-snapshot?roleType=${roleType}`,
			generateMatchSnapshot: (id: number) =>
				`${BASE_URL}/departures/${id}/matches-snapshot/generate`,
			leaderMatches: (id: number) => `${BASE_URL}/departures/${id}/leader-matches/`,
			leaderMatch: (id: number, tlId: number) => `${BASE_URL}/departures/${id}/leader-matches/${tlId}`,
			traineeMatches: (id: number) => `${BASE_URL}/departures/${id}/trainee-matches/`,
			traineeMatch: (id: number, tlId: number) => `${BASE_URL}/departures/${id}/trainee-matches/${tlId}`,
			leaderAssignment: (id: number, assignmentId: number) =>
				`${BASE_URL}/departures/${id}/leader-assignments/${assignmentId}`,
			applyLeaderAlternative: (id: number, assignmentId: number) =>
				`${BASE_URL}/departures/${id}/leader-assignments/${assignmentId}/apply-alternative`,
			applyTraineeAlternative: (id: number, assignmentId: number) =>
				`${BASE_URL}/departures/${id}/trainee-assignments/${assignmentId}/apply-alternative`,
			qualificationImpact: (id: number, assignmentId: number) =>
				`${BASE_URL}/departures/${id}/leader-assignments/${assignmentId}/qualification-impact`,
			withdrawQualificationSource: (id: number, assignmentId: number) =>
				`${BASE_URL}/departures/${id}/leader-assignments/${assignmentId}/withdraw-qualification-source`,
			leaderAssignments: (id: number) => `${BASE_URL}/departures/${id}/leader-assignments/`,
			traineeAssignments: (id: number) => `${BASE_URL}/departures/${id}/trainee-assignments/`,
			rejectAssignment: (id: number) => `${BASE_URL}/departures/${id}/reject-assignment`,
			removeRejection: (id: number) => `${BASE_URL}/departures/${id}/reject-assignment`,
		},
		roadScholar: {
			all: `${BASE_URL}/departures?type=road-scholar`,
			details: (id: number) => `${BASE_URL}/rs-departures/${id}/details`,
			generateEligibilityReport: (id: number) =>
				`${BASE_URL}/rs-departures/${id}/eligibility-reports/generate`,
			matchSnapshot: (id: number, roleType: "GL" | "PA") =>
				`${BASE_URL}/rs-departures/${id}/matches-snapshot?roleType=${roleType}`,
			generateMatchSnapshot: (id: number) =>
				`${BASE_URL}/rs-departures/${id}/matches-snapshot/generate`,
			liveViewServerTable: `${BASE_URL}/rs-departures-live-view/data-table`,
			liveViewFilterOptions: `${BASE_URL}/rs-departures-live-view/filter-options`,
			glMatches: (departureId: number, tlId?: number) =>
				tlId
					? `${BASE_URL}/rs-departures/${departureId}/all-matches/${tlId}?type=gl`
					: `${BASE_URL}/rs-departures/${departureId}/all-matches?type=gl`,
			paMatches: (departureId: number, tlId?: number) =>
				tlId
					? `${BASE_URL}/rs-departures/${departureId}/all-matches/${tlId}?type=pa`
					: `${BASE_URL}/rs-departures/${departureId}/all-matches?type=pa`,
			leaderAssignments: (departureId: number) =>
				`${BASE_URL}/rs-departures/${departureId}/leader-assignments/`,
			leaderAssignment: (departureId: number, assignmentId: number) =>
				`${BASE_URL}/rs-departures/${departureId}/leader-assignments/${assignmentId}`,
			applyLeaderAlternative: (departureId: number, assignmentId: number) =>
				`${BASE_URL}/rs-departures/${departureId}/leader-assignments/${assignmentId}/apply-alternative`,
			qualificationImpact: (departureId: number, assignmentId: number) =>
				`${BASE_URL}/rs-departures/${departureId}/leader-assignments/${assignmentId}/qualification-impact`,
			withdrawQualificationSource: (departureId: number, assignmentId: number) =>
				`${BASE_URL}/rs-departures/${departureId}/leader-assignments/${assignmentId}/withdraw-qualification-source`,
			rejectAssignment: (departureId: number) => `${BASE_URL}/rs-departures/${departureId}/reject-assignment`,
			removeRejection: (departureId: number) => `${BASE_URL}/rs-departures/${departureId}/reject-assignment`,
			traineeMatches: (id: number) => `${BASE_URL}/rs-departures/${id}/trainee-matches`,
			leaderMatches: (id: number) => `${BASE_URL}/rs-departures/${id}/leader-matches`,
		},
		proposals: {
			leadersRequiringProposals: `${BASE_URL}/departures/proposals/leaders`,
			leaderProposals: (id: number, status: string) =>
				`${BASE_URL}/departures/leader/${id}/proposals?status=${status}`,
			proposalAction: (id: number) => `${BASE_URL}/departures/leader/${id}/proposal-action`,
			proposalUndo: (id: number) => `${BASE_URL}/departures/leader/${id}/proposal-undo`,
		},
	},
	assignments: {
		acceptAllProposals: `${BASE_URL}/assignments/proposals/accept-all`,
		proposalAccepted: `${BASE_URL}/assignments/proposals/accepted`,
		proposalAcceptedByLeader: (id: number) => `${BASE_URL}/assignments/proposals/accepted?leaderId=${id}`,
		proposalAcceptedByDeparture: (departureId: number) =>
			`${BASE_URL}/assignments/proposals/accepted?departureId=${departureId}`,
		confirmed: `${BASE_URL}/assignments/confirm`,
		withdrawn: `${BASE_URL}/assignments/withdrawn`,
		checkOverlappingAssignments: `${BASE_URL}/assignments/check-overlapping-assignments`,
	},
	leaders: {
		getAll: `${BASE_URL}/leaders`,
		dataTable: `${BASE_URL}/leaders/data-table`,
		proposalNotifications: `${BASE_URL}/leaders/data-table/proposal-notifications`,
		filterOptions: `${BASE_URL}/leaders/filter-options`,
		getById: (id: number) => `${BASE_URL}/leaders/${id}`,
		create: `${BASE_URL}/leaders`,
		update: (id: number) => `${BASE_URL}/leaders/${id}`,

		toggleActive: (id: number) => `${BASE_URL}/leaders/${id}/toggle-active`,
		dayCounts: `${BASE_URL}/leaders/day-counts`,
		preferences: {
			preferred: (id: number) => `${BASE_URL}/leaders/${id}/preferences/preferred`,
			unpreferred: (id: number) => `${BASE_URL}/leaders/${id}/preferences/unpreferred`,
			trainedOn: (id: number) => `${BASE_URL}/leaders/${id}/preferences/trained-on`,
			trainedOnSchoolCodes: (id: number) => `${BASE_URL}/leaders/${id}/preferences/trained-on-school-codes`,
			preferredTraining: (id: number) => `${BASE_URL}/leaders/${id}/preferences/preferred-training`,
		},
		notes: (id: number) => `${BASE_URL}/leaders/${id}/notes`,
		languages: (id: number) => `${BASE_URL}/leaders/${id}/languages`,
		unavailabilities: {
			getById: (id: number) => `${BASE_URL}/leader-unavailabilities/${id}`,
			update: (unavailabilityId: number) => `${BASE_URL}/leader-unavailabilities/${unavailabilityId}`,
		},
		regular: {
			dayCounts: `${BASE_URL}/leaders/day-counts?type=regular`,
			assignmentDayCounts: `${BASE_URL}/leaders/assignment-day-counts?type=regular`,
		},
		roadScholar: {
			dayCounts: `${BASE_URL}/rs-leaders/day-counts?type=road-scholar`,
			assignmentDayCounts: `${BASE_URL}/leaders/assignment-day-counts?type=road-scholar`,
		},
	},
	tours: {
		all: `${BASE_URL}/tours`,
		hasCurrentDepartures: `${BASE_URL}/tours/?hasCurrentDepartures=true`,
		dataTable: `${BASE_URL}/tours/data-table`,
		filterOptions: `${BASE_URL}/tours/filter-options`,
		regular: `${BASE_URL}/tours?type=regular`,
		roadScholar: `${BASE_URL}/tours?type=road-scholar`,
		getById: (id: number) => `${BASE_URL}/tours/${id}`,
		update: (id: number) => `${BASE_URL}/tours/${id}`,
	},
	settings: `${BASE_URL}/settings`,
	regularAssignmentMode: `${BASE_URL}/settings/regular-assignment-mode`,
	seasonReset: `${BASE_URL}/settings/season-reset`,
	seasonResetStatus: `${BASE_URL}/settings/season-reset/status`,
	userManagement: {
		getUsers: `${BASE_URL}/users`,
		getUser: (id: number) => `${BASE_URL}/users/${id}`,
		createUser: `${BASE_URL}/users`,
		updateUser: (id: number) => `${BASE_URL}/users/${id}`,
		toggleUserActive: (id: number) => `${BASE_URL}/users/${id}/toggle-active`,
		resetPassword: (id: number) => `${BASE_URL}/users/${id}/reset-password`,
		changePassword: (id: number) => `${BASE_URL}/users/${id}/change-password`,
		getRoles: `${BASE_URL}/roles`,
	},
	logs: {
		auditLogs: `${BASE_URL}/audit-logs`,
	},
	auth: {
		login: `${BASE_URL}/auth/login`,
		logout: `${BASE_URL}/auth/logout`,
		refresh: `${BASE_URL}/auth/refresh`,
		me: `${BASE_URL}/auth/me`,
		changePassword: `${BASE_URL}/auth/change-password`,
	},
	lookups: {
		countries: `${BASE_URL}/lookups/countries`,
		leaderTypes: `${BASE_URL}/lookups/leader-types`,
		languages: `${BASE_URL}/lookups/languages`,
	},
	dataImport: {
		tourLeaders: `${BASE_URL}/data-import/tour-leaders`,
		toursAndDepartures: `${BASE_URL}/data-import/tours-and-departures`,
		lastRuns: `${BASE_URL}/data-import/last-runs`,
		dataTable: `${BASE_URL}/data-import/logs/data-table`,
		filterOptions: `${BASE_URL}/data-import/logs/filter-options`,
		details: (id: number) => `${BASE_URL}/data-import/logs/${id}`,
	},
	spreadsheet: `${BASE_URL}/spreadsheet/`,
} as const;

Object.freeze(apiEndpoints);
